import { getApiBaseUrl } from "../utils/getApiBaseUrl";
import { authHeaders, getAuthToken } from "./authApi";

const API_BASE_URL = getApiBaseUrl();

/** Align with library Stripe helpers: accept flat or wrapped session payloads. */
function normalizeOsharCheckoutSessionUrl(payload) {
  if (!payload || typeof payload !== "object") return "";
  if (typeof payload.url === "string" && payload.url.trim()) return payload.url.trim();
  const d = payload.data;
  if (d && typeof d === "object") {
    const u = d.url || d.checkoutUrl || d.sessionUrl;
    if (typeof u === "string" && u.trim()) return u.trim();
  }
  for (const k of ["checkoutUrl", "sessionUrl"]) {
    const u = payload[k];
    if (typeof u === "string" && u.trim()) return u.trim();
  }
  return "";
}

/** User-facing copy for Oshar card checkout failures (no secrets). */
function mapUserFacingOsharCheckoutError(status, rawMessage = "") {
  const msg = String(rawMessage || "").trim();
  const lower = msg.toLowerCase();

  if (status === 401) {
    if (lower.includes("session expired")) return "Your session expired. Please log in again.";
    return "Please log in to purchase.";
  }
  if (status === 403) {
    if (lower.includes("invalid token")) return "Your session expired. Please log in again.";
    return "Session was rejected. Log out and sign in again, then retry.";
  }
  if (lower.includes("unknown oshar real estate tier")) {
    return "This property tier is unavailable.";
  }
  if (lower.includes("sold out")) {
    return "Sold out.";
  }
  const cap = msg.match(/\((\d+) max for this tier\)/i);
  if (cap) {
    return `Only ${cap[1]} remaining.`;
  }
  if (status >= 500) {
    return "Checkout service unavailable.";
  }
  if (!msg) return "Stripe checkout request failed.";
  return msg;
}

export function getStoredAuthToken() {
  return getAuthToken();
}

/**
 * POST /stripe/oshar-real-estate/create-checkout-session — Bearer auth.
 * Body: { tierSlug, quantity } (canonical storefront). tokenId-only is server-only / legacy tests.
 */
export async function createOsharCheckoutSession({ tierSlug, quantity = 1 } = {}) {
  const slug = String(tierSlug || "").trim();
  if (!slug) {
    throw new Error("Property tier is missing. Reload the page and try again.");
  }

  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("Please log in to reserve with card.");
  }

  const body = { tierSlug: slug, quantity };

  let response;
  try {
    response = await fetch(
      `${API_BASE_URL.replace(/\/$/, "")}/stripe/oshar-real-estate/create-checkout-session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(body),
      }
    );
  } catch {
    throw new Error("Checkout service unavailable.");
  }

  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    const raw =
      payload?.message ||
      (typeof payload?.error === "string" ? payload.error : null) ||
      "";
    let message = mapUserFacingOsharCheckoutError(response.status, raw);
    if (response.status === 503 || response.status === 502) {
      message = "Checkout service unavailable.";
    }
    throw new Error(message);
  }

  const url = normalizeOsharCheckoutSessionUrl(payload);
  if (!url) {
    throw new Error("Stripe did not return a checkout URL.");
  }

  return { ...payload, url };
}

/**
 * POST /api/oshar-real-estate/sync-wallet-mint — Bearer auth.
 * Body: { txHash, chainId?, tokenId? } — backend verifies Sepolia receipt; tierSlug from chain is not trusted from UI alone.
 */
/** localStorage key: pending wallet mint until dashboard sync succeeds (HTTP 200). */
export const PENDING_OSHAR_WALLET_MINT_STORAGE_KEY = "treydora:pending-oshar-wallet-mint";

export function readPendingOsharWalletMintRecord() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(PENDING_OSHAR_WALLET_MINT_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    return o && typeof o === "object" ? o : null;
  } catch {
    return null;
  }
}

/** @param {Record<string, unknown>} record — safe fields only (no JWTs). */
export function writePendingOsharWalletMintRecord(record) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(PENDING_OSHAR_WALLET_MINT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* quota / private mode */
  }
}

export function clearPendingOsharWalletMintRecord() {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.removeItem(PENDING_OSHAR_WALLET_MINT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Replays POST /api/oshar-real-estate/sync-wallet-mint from the last pending mint record.
 * Clears localStorage only on HTTP 200 + success payload (see syncOsharWalletMint).
 */
export async function retryPendingOsharWalletSync() {
  const rec = readPendingOsharWalletMintRecord();
  const h = String(rec?.txHash || "").trim();
  if (!rec || !/^0x([A-Fa-f0-9]{64})$/.test(h)) {
    throw new Error("No pending Oshar wallet mint to sync.");
  }
  const tokenFromList =
    Array.isArray(rec.tokenIds) && rec.tokenIds.length > 0 ? Math.floor(Number(rec.tokenIds[0])) : NaN;
  const tokenFromScalar = rec.tokenId != null ? Math.floor(Number(rec.tokenId)) : NaN;
  const tokenId = Number.isFinite(tokenFromList) ? tokenFromList : Number.isFinite(tokenFromScalar) ? tokenFromScalar : undefined;

  await syncOsharWalletMint({
    txHash: h,
    chainId: rec.chainId != null ? Number(rec.chainId) : undefined,
    walletAddress: rec.walletAddress,
    tokenId,
    tierSlug: rec.tierSlug != null ? String(rec.tierSlug).trim() : undefined,
  });
  clearPendingOsharWalletMintRecord();
  return { ok: true };
}

export async function syncOsharWalletMint({ txHash, chainId, tokenId, tierSlug, walletAddress } = {}) {
  const h = String(txHash || "").trim();
  if (!/^0x([A-Fa-f0-9]{64})$/.test(h)) {
    throw new Error("Enter a valid Sepolia transaction hash (0x + 64 hex characters).");
  }

  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("Please log in to sync a wallet mint to your dashboard.");
  }

  const body = {
    txHash: h.toLowerCase(),
    ...(chainId != null ? { chainId: Number(chainId) } : {}),
    ...(tokenId != null ? { tokenId: Number(tokenId) } : {}),
    ...(tierSlug != null && String(tierSlug).trim() ? { tierSlug: String(tierSlug).trim() } : {}),
    ...(walletAddress != null && String(walletAddress).trim()
      ? { walletAddress: String(walletAddress).trim() }
      : {}),
  };

  const attemptedUrl = `${API_BASE_URL.replace(/\/$/, "")}/api/oshar-real-estate/sync-wallet-mint`;
  let response;
  try {
    response = await fetch(attemptedUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
      body: JSON.stringify(body),
    });
  } catch (cause) {
    const err = new Error(
      "Treydora API is unreachable from this browser (network). Check the backend is running and REACT_APP_API_BASE_URL."
    );
    err.isNetworkFailure = true;
    err.attemptedUrl = attemptedUrl;
    err.httpStatus = null;
    err.responseMessage = err.message;
    err.errorName = cause?.name || "NetworkError";
    err.errorMessage = String(cause?.message || cause || "").slice(0, 300);
    err.responseBodyKeys = [];
    throw err;
  }

  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    const message =
      payload?.message ||
      (typeof payload?.error === "string" ? payload.error : null) ||
      "Unable to verify this transaction on the server. Please try again.";
    const err = new Error(message);
    err.attemptedUrl = attemptedUrl;
    err.httpStatus = response.status;
    err.responseMessage = message;
    err.errorName = "SyncHttpError";
    err.errorMessage = message;
    err.responseBodyKeys =
      payload && typeof payload === "object" && !Array.isArray(payload) ? Object.keys(payload).slice(0, 20) : [];
    throw err;
  }

  if (!payload?.success) {
    const message = payload?.message || "Sync failed.";
    const err = new Error(message);
    err.attemptedUrl = attemptedUrl;
    err.httpStatus = response.status;
    err.responseMessage = message;
    err.errorName = "SyncPayloadError";
    err.errorMessage = message;
    err.responseBodyKeys =
      payload && typeof payload === "object" && !Array.isArray(payload) ? Object.keys(payload).slice(0, 20) : [];
    throw err;
  }

  return payload;
}

/**
 * POST /api/oshar-real-estate/wallet-mint/prepare — Bearer auth.
 * Body (JSON stringified): { tierSlug, quantity, walletAddress } — backend also accepts tierId as tierSlug alias.
 */
export async function prepareOsharWalletMint({ tierSlug, quantity = 1, walletAddress, signal } = {}) {
  const slug = String(tierSlug || "").trim();
  const addr = String(walletAddress || "").trim();
  const qty = Math.floor(Number(quantity));
  if (!slug) throw new Error("Property tier is missing.");
  if (!addr) throw new Error("Connect a wallet first.");
  if (!Number.isFinite(qty) || qty < 1) throw new Error("Select a valid quantity.");

  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("Please sign in before wallet minting.");
  }

  const attemptedUrl = `${API_BASE_URL.replace(/\/$/, "")}/api/oshar-real-estate/wallet-mint/prepare`;

  let response;
  try {
    response = await fetch(attemptedUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
      body: JSON.stringify({ tierSlug: slug, quantity: qty, walletAddress: addr }),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    const cname = cause?.name || "";
    if (cname === "AbortError" || cname === "TimeoutError") {
      const err = new Error(
        "Wallet mint prepare timed out before the Treydora API returned a response."
      );
      err.name = "AbortError";
      err.isAbortTimeout = true;
      err.attemptedUrl = attemptedUrl;
      err.httpStatus = null;
      err.responseMessage = err.message;
      err.errorName = cname;
      err.errorMessage = String(cause?.message || "").slice(0, 300);
      err.responseBodyKeys = [];
      throw err;
    }
    const err = new Error(
      "Treydora API is unreachable from this browser (network). Check the backend is running and REACT_APP_API_BASE_URL."
    );
    err.isNetworkFailure = true;
    err.attemptedUrl = attemptedUrl;
    err.httpStatus = null;
    err.responseMessage = err.message;
    err.errorName = cname || "NetworkError";
    err.errorMessage = String(cause?.message || cause || "").slice(0, 300);
    err.responseBodyKeys = [];
    throw err;
  }

  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  const bodyKeys =
    data && typeof data === "object" && !Array.isArray(data) ? Object.keys(data).slice(0, 20) : [];

  if (!response.ok) {
    const msg =
      (Array.isArray(data?.blockers) && data.blockers[0]) ||
      data?.message ||
      (typeof data?.error === "string" ? data.error : null) ||
      "Wallet mint prepare failed.";
    const err = new Error(msg);
    err.attemptedUrl = attemptedUrl;
    err.httpStatus = response.status;
    err.responseMessage = msg;
    err.errorName = "PrepareHttpError";
    err.errorMessage = msg;
    err.responseBodyKeys = bodyKeys;
    err.prepareBodyKeys = bodyKeys;
    throw err;
  }

  if (data?.ready === false) {
    const msg =
      (Array.isArray(data?.blockers) && data.blockers[0]) || data?.message || "Wallet mint is not available.";
    const err = new Error(msg);
    err.attemptedUrl = attemptedUrl;
    err.httpStatus = response.status;
    err.responseMessage = msg;
    err.errorName = "PrepareNotReady";
    err.errorMessage = msg;
    err.responseBodyKeys = bodyKeys;
    err.prepareBodyKeys = bodyKeys;
    throw err;
  }

  return data;
}

export function getOsharApiBaseUrl() {
  return API_BASE_URL;
}

/**
 * POST /api/oshar-real-estate/reservations/:reservationId/claim — Bearer auth.
 * Body: { walletAddress } — must be linked on the account; Sepolia server mint + transfer.
 */
export async function claimOsharStripeReservation({ reservationId, walletAddress } = {}) {
  const id = String(reservationId || "").trim();
  const addr = String(walletAddress || "").trim();
  if (!id) {
    throw new Error("Reservation id is missing.");
  }
  if (!addr) {
    throw new Error("Connect a wallet and use its address to claim.");
  }

  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("Please log in to claim your property NFT.");
  }

  let response;
  try {
    response = await fetch(
      `${API_BASE_URL.replace(/\/$/, "")}/api/oshar-real-estate/reservations/${encodeURIComponent(
        id
      )}/claim`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({ walletAddress: addr }),
      }
    );
  } catch {
    throw new Error("Unable to reach the Treydora API. Check your network and REACT_APP_API_BASE_URL.");
  }

  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    const message =
      payload?.message ||
      (typeof payload?.error === "string" ? payload.error : null) ||
      "Claim request failed.";
    throw new Error(message);
  }

  if (!payload?.success) {
    throw new Error(payload?.message || "Claim failed.");
  }

  return payload;
}

export { getOsharRealEstateTiers } from "./osharRealEstateTiersApi.js";
